<template>
    <div id="edit-auto-reply" v-mdl class="mdl-card mdl-shadow--6dp">
        <div class="mdl-card__title">
            <h2 class="mdl-card__title-text">
                {{ $t('settings.auto_reply.edit') }}
            </h2>
        </div>

        <!-- Rendered once the reply is loaded, so v-mdl upgrades the fields with their values in place. -->
        <div v-if="reply" v-mdl class="mdl-card__supporting-text">
            <p v-if="typeContact" class="reply-contact">
                {{ $t('settings.auto_reply.contact') }}: {{ pattern }}
            </p>
            <div v-if="typeKeyword" class="mdl-textfield mdl-js-textfield">
                <input id="pattern" v-model="pattern" class="mdl-textfield__input">
                <label class="mdl-textfield__label" for="pattern">{{ $t('settings.auto_reply.keyword') }}</label>
            </div>
            <div class="mdl-textfield mdl-js-textfield">
                <input id="response" v-model="response" class="mdl-textfield__input">
                <label class="mdl-textfield__label" for="response">{{ $t('settings.auto_reply.response') }}</label>
            </div>
        </div>

        <div class="mdl-card__actions mdl-card--border">
            <button id="save" class="mdl-button mdl-js-button mdl-js-ripple-effect" :disabled="!reply" @click="save">
                {{ $t('dialog.save') }}
            </button>
            <button id="cancel" class="mdl-button mdl-js-button mdl-js-ripple-effect" @click="goToAutoReplies">
                {{ $t('dialog.cancel') }}
            </button>
            <button id="delete" class="delete-button mdl-button mdl-js-button mdl-js-ripple-effect" :disabled="!reply" @click="deleteReply">
                {{ $t('dialog.delete') }}
            </button>
        </div>

        <transition name="loading-fade">
            <div v-if="loading" class="loading-center">
                <spinner />
            </div>
        </transition>
    </div>
</template>

<script>
import { Api, Util } from '@/utils/';
import Spinner from '@/components/Spinner.vue';

// PULS-1424: editing an existing auto reply, as Android's AutoReplySettingsFragment does. Contact,
// driving and vacation replies only change their response; keyword replies can change both.
export default {
    name: 'EditAutoReply',
    components: {
        Spinner
    },
    props: ['replyId'],
    data () {
        return {
            reply: null,
            pattern: "",
            response: "",
            loading: true,
        };
    },
    computed: {
        typeKeyword () {
            return this.reply && this.reply.reply_type === "keyword";
        },
        typeContact () {
            return this.reply && this.reply.reply_type === "contact";
        }
    },
    mounted () {
        this.$store.commit("loading", false);
        this.$store.commit('title', this.$t('settings.auto_reply.edit'));

        // Served from the SessionCache when the list was already loaded (e.g. after a page reload).
        Api.autoReplies.get()
            .then(replies => {
                const reply = replies.find(r => String(r.device_id) === String(this.replyId));
                if (!reply) {
                    this.goToAutoReplies();
                    return;
                }

                this.pattern = reply.pattern || "";
                this.response = reply.response || "";
                this.reply = reply;
            })
            .catch(() => this.goToAutoReplies())
            .finally(() => {
                this.loading = false;
            });
    },
    methods: {
        goToAutoReplies () {
            this.$router.push({ name: "auto-replies" });
        },
        isValidInput () {
            let valid = true;
            if (this.typeKeyword && !this.pattern) {
                valid = false;
                Util.snackbar('Keyword is required');
            }
            if (!this.response) {
                valid = false;
                Util.snackbar('Response is required');
            }
            return valid;
        },
        save () {
            if (!this.isValidInput()) {
                return;
            }
            this.loading = true;
            Api.autoReplies.update(this.reply.device_id, this.reply.reply_type, this.pattern, this.response)
                .then(() => {
                    Util.snackbar(this.$t('settings.auto_reply.updated'));
                    this.goToAutoReplies();
                })
                .catch(() => {
                    Util.snackbar(this.$t('settings.auto_reply.update_failed'));
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        // Android's edit dialog has the same Delete button, behind a confirmation.
        deleteReply () {
            const options = {
                okText: this.$t('dialog.delete'),
                cancelText: this.$t('dialog.cancel'),
                animation: 'fade'
            };

            this.$dialog.confirm(this.$t('settings.auto_reply.delete_confirm'), options)
                .then(() => {
                    this.loading = true;
                    return Api.autoReplies.delete(this.reply.device_id)
                        .then(() => {
                            Util.snackbar(this.$t('settings.auto_reply.deleted'));
                            this.goToAutoReplies();
                        })
                        .catch(() => {
                            Util.snackbar(this.$t('settings.auto_reply.delete_failed'));
                        })
                        .finally(() => {
                            this.loading = false;
                        });
                })
                .catch(() => { }); // Cancelled
        }
    }
};
</script>

<style lang="scss" scoped>
    @import "@/assets/scss/_vars.scss";

    #edit-auto-reply {
        position: relative;
        width: 330px;
        height: 100%;
        margin: 5em auto;
    }

    .delete-button {
        float: right;
    }

    .reply-contact {
        margin-bottom: 8px;
    }

    .loading-center {
        display: flex;
        justify-content: center;
        align-items: center;
        position: absolute;
        margin: 57px auto 0;
        background: #fff;
        height: calc(100% - 57px);
        width: 100%;
    }
</style>
